import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import {
  MatButtonToggleAppearance,
  MatButtonToggleChange,
} from '@angular/material/button-toggle';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { IconType } from '@ng-icons/core';
import { Observable, Subscription, combineLatest, map } from 'rxjs';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { SHOW_ALL } from 'src/app/modules/route-planner/constants/multi-language.constants';

@Component({
    selector: 'mot-multi-checkbox',
    templateUrl: './mot-multi-checkbox.component.html',
    styleUrls: ['./mot-multi-checkbox.component.scss'],
    standalone: false
})
export class MotMultiCheckboxComponent implements ControlValueAccessor, OnInit {
  @Input() options: Observable<SelectOption<any>[]>;
  @Input() value: string;
  @Input() class: string;
  @Input() iconClass: string;
  @Input() checkboxClass: string;
  @Input() addSelectAllButton: boolean = true;
  @Input() selectAllIcon: IconType;

  formControl: FormControl;
  optionsSubscriber: Subscription;
  allSelected: boolean = false;
  someSelected: boolean = false;
  showAllText: string

  constructor(@Optional() @Self() public ngControl: NgControl, private readonly languageService: LanguageService) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      this.formControl = this.ngControl.control as FormControl;
      if (this.value) {
        combineLatest([this.formControl.valueChanges, this.options])
          .pipe(
            map(([values, opts]) => {
              const selected = values.filter(
                (val) => val !== undefined && val !== null
              ).length;
              const allSelected = selected === opts.length;

              return {
                all: allSelected,
                some: selected > 0 && !allSelected,
              };
            })
          )
          .subscribe((status) => {
            this.allSelected = status.all;
            this.someSelected = status.some;

            this.formControl.markAsDirty();
          });

        this.formControl.setValue(this.value);
      }
    }
    this.initializeMultiLanguageTexts()
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
     this.showAllText = SHOW_ALL[language.language]
    })
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!!this.formControl) {
      if (isDisabled) this.formControl.disable();
      else this.formControl.enable();
    }
  }

  selectAllToggleClick() {
    if (!!this.optionsSubscriber) this.optionsSubscriber.unsubscribe();

    this.optionsSubscriber = this.options
      .pipe(map((opts) => opts.map((opt) => opt.value)))
      .subscribe((opts) => {
        if (!this.allSelected) {
          this.formControl.setValue(opts);
        } else {
          this.formControl.setValue([]);
        }
      });
  }

  onCheckBoxChange(evt: MatCheckboxChange, selectValue: any) {
    const values: any[] = this.formControl.value;
    const selectedValuesIndex = values.findIndex(
      (value) => value == selectValue
    );

    if (!!evt.checked && selectedValuesIndex == -1) {
      values.push(selectValue);
    } else if (!evt.checked && selectedValuesIndex != -1) {
      values.splice(selectedValuesIndex, 1);
    }

    this.formControl.setValue(values);
  }
}
